import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  endOfMonth,
  endOfWeek,
  getYear,
  isBefore,
  isSameDay,
  isSameMonth,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import { CS_PERIODS_START_DATE } from '@/constants/env.constants';
import { formatDate } from '@/utils/helpers';

/**
 * 28-day planning period model for the Clinical Specialists (CS) schedule.
 *
 * - Periods are fixed 28-day blocks anchored to {@link CS_PERIOD_EPOCH} - the per-environment
 *   cutover date set at build time via `VITE_CS_PERIODS_START_DATE` (`yyyy-MM-dd`).
 * - The grid extends indefinitely in BOTH directions from the anchor, so a year holds
 *   notional periods that precede the anchor.
 * - A period belongs to the year holding the majority of its days, and periods of a year
 *   are numbered from 1 in chronological order.
 *
 * Examples, with the epoch on Jan 1, 2027:
 *    1.2027 — Jan  1 – Jan 28, 2027          ← the first period the grid actually serves
 *    2.2027 — Jan 29 – Feb 25, 2027
 *   13.2027 — Dec  3 – Dec 30, 2027
 *    1.2028 — Dec 31, 2027 – Jan 27, 2028    ← spans two calendar years, majority in 2028
 *    2.2028 — Jan 28 – Feb 24, 2028
 *
 * An epoch that does not open its year is numbered after the notional periods preceding it:
 * with the epoch on Dec 1, 2026 the first served period is 13.2026.
 *
 * Migration: on/after {@link CS_PERIOD_EPOCH} the period model is active; before it the
 * legacy calendar-month model is used so historical data remains accessible.
 * Without an epoch (the env variable is unset or invalid) every date uses the calendar-month model.
 *
 * This mirrors Schedule28DaysPeriodCalendar on the backend and must not diverge from it.
 *
 * TWO INDEPENDENT ANCHORS, and conflating them is the mistake to avoid:
 * {@link CS_PERIOD_EPOCH} decides a period's SHAPE (28-day grid rather than calendar month),
 * {@link CS_DEDUCTION_RULES_EPOCH} decides the INPUTS it is planned from (Shift Time Groups
 * and the absence-deduction table).
 * They are defined independently of each other, so a period can be grid-shaped and still be
 * planned from the monthly settings.
 *
 * This model applies ONLY to the CS schedule. Other schedules remain month-based.
 */

export const CS_PERIOD_LENGTH = 28;

/** Parses a `yyyy-MM-dd` cutover date into a local-midnight epoch; missing or invalid → `null`. */
export const parseCSPeriodEpoch = (value?: string | null): Date | null => {
  if (!value) return null;

  const epoch = startOfDay(parseISO(value));
  return isValid(epoch) ? epoch : null;
};

/** Epoch and migration date: start of the first 28-day period, `null` when CS stays on calendar months. */
export const CS_PERIOD_EPOCH = parseCSPeriodEpoch(CS_PERIODS_START_DATE);
/**
 * Planning-inputs cutover — the FE mirror of SchedulerOptions.CSDeductionRulesStartDate.
 * Distinct from {@link CS_PERIOD_EPOCH}, which decides only the shape of a period.
 * From this date a period is planned from the Time Group coverage settings and the
 * absence-deduction table; before it a period may already be 28 days long but is still
 * planned from the monthly settings, and shows no Shift Time Groups at all.
 * Jan 1, 2027 — a separate feature cutover, deliberately not derived from the period epoch.
 */
export const CS_DEDUCTION_RULES_EPOCH = CS_PERIOD_EPOCH ?? startOfDay(new Date(2027, 0, 1));

/** Whether a given date falls in the new 28-day period model (on/after epoch). */
export const isCSPeriodMode = (date: Date): boolean =>
  CS_PERIOD_EPOCH !== null && !isBefore(startOfDay(date), CS_PERIOD_EPOCH);

/**
 * Whether a given date's period is planned from the Time Group settings and the deduction table.
 * This - never {@link isCSPeriodMode} - is what gates every Shift Time Group affordance.
 */
export const isCSDeductionRulesMode = (date: Date): boolean => !isBefore(startOfDay(date), CS_DEDUCTION_RULES_EPOCH);

export type CSPeriodInfo = {
  /** 1-based period number within the year the period belongs to. */
  periodNumber: number;
  /** The year holding the majority of the period's days. */
  year: number;
  /** First day of the period. */
  start: Date;
  /** Last day of the period. */
  end: Date;
};

/**
 * The 15th day of a period decides which year it belongs to, which is how the backend
 * expresses "the year holding the majority of its days".
 * A 14/14 split therefore falls to the later year.
 */
const YEAR_DECIDING_DAY_OFFSET = 14;

/** Signed 0-based index of the period containing `date`, counted from `epoch`. */
const getPeriodIndex = (date: Date, epoch: Date): number =>
  Math.floor(differenceInCalendarDays(startOfDay(date), epoch) / CS_PERIOD_LENGTH);

const getYearDecidingDay = (periodIndex: number, epoch: Date): Date =>
  addDays(epoch, periodIndex * CS_PERIOD_LENGTH + YEAR_DECIDING_DAY_OFFSET);

/**
 * Index of the first period belonging to `year`.
 * Deliberately NOT clamped to zero - the grid extends backwards from the epoch, so a year
 * holds notional periods that precede it and the anchor period is numbered accordingly.
 */
const getFirstPeriodIndexOfYear = (year: number, epoch: Date): number =>
  Math.ceil(
    (differenceInCalendarDays(startOfYear(new Date(year, 0, 1)), epoch) - YEAR_DECIDING_DAY_OFFSET) / CS_PERIOD_LENGTH,
  );

/**
 * Resolves the 28-day period that contains `date`.
 * Only valid for dates on/after {@link CS_PERIOD_EPOCH}: callers check {@link isCSPeriodMode} first.
 */
export const getCSPeriodInfo = (date: Date): CSPeriodInfo => {
  // Without an epoch there are no periods; anchoring to the date itself keeps the result well-defined.
  const epoch = CS_PERIOD_EPOCH ?? startOfDay(date);
  const periodIndex = getPeriodIndex(date, epoch);
  const start = addDays(epoch, periodIndex * CS_PERIOD_LENGTH);
  const end = addDays(start, CS_PERIOD_LENGTH - 1);
  const year = getYear(getYearDecidingDay(periodIndex, epoch));

  return { periodNumber: periodIndex - getFirstPeriodIndexOfYear(year, epoch) + 1, year, start, end };
};

export const getCSPeriodStart = (date: Date): Date => getCSPeriodInfo(date).start;

/**
 * The "window" start day sent to the backend to identify the schedule.
 * Period start in period mode, start of the calendar month in legacy mode.
 */
export const getCSStartDay = (date: Date): Date => (isCSPeriodMode(date) ? getCSPeriodStart(date) : startOfMonth(date));

/** The rendered date range (padded to full weeks) for the current window. */
export const getCSWindowRange = (date: Date): { from: Date; to: Date } => {
  if (isCSPeriodMode(date)) {
    const { start, end } = getCSPeriodInfo(date);
    return { from: startOfWeek(start), to: endOfWeek(end) };
  }

  return { from: startOfWeek(startOfMonth(date)), to: endOfWeek(endOfMonth(date)) };
};

/** The range over which per-resource shift totals are aggregated for the window. */
export const getCSTotalsRange = (date: Date): { start: Date; end: Date } => {
  if (isCSPeriodMode(date)) {
    const { start, end } = getCSPeriodInfo(date);
    return { start, end };
  }

  return { start: startOfMonth(date), end: endOfMonth(date) };
};

/** Whether two dates belong to the same window (period in period mode, month otherwise). */
export const isSameCSWindow = (a: Date, b: Date): boolean => {
  const aPeriod = isCSPeriodMode(a);
  const bPeriod = isCSPeriodMode(b);
  if (aPeriod !== bPeriod) return false;

  return aPeriod ? isSameDay(getCSPeriodStart(a), getCSPeriodStart(b)) : isSameMonth(a, b);
};

/** Next window's anchor date. Crossing the migration boundary snaps to the first period. */
export const getCSNextDate = (date: Date): Date => {
  if (isCSPeriodMode(date)) {
    return addDays(getCSPeriodInfo(date).end, 1);
  }

  const nextMonthDate = addMonths(date, 1);
  return CS_PERIOD_EPOCH && isCSPeriodMode(nextMonthDate) ? CS_PERIOD_EPOCH : nextMonthDate;
};

/** Previous window's anchor date. Crossing back over the boundary snaps to the legacy month. */
export const getCSPrevDate = (date: Date): Date => {
  if (CS_PERIOD_EPOCH && isCSPeriodMode(date)) {
    const prevPeriodStart = getCSPeriodStart(addDays(getCSPeriodInfo(date).start, -1));
    return isCSPeriodMode(prevPeriodStart) ? prevPeriodStart : addDays(CS_PERIOD_EPOCH, -1);
  }

  return addMonths(date, -1);
};

/** Header label for the current window: "Period N · YYYY" or "MMMM yyyy" in legacy mode. */
export const getCSWindowLabel = (date: Date): string => {
  if (!isCSPeriodMode(date)) return formatDate(date, 'MMMM yyyy');

  const { periodNumber, year } = getCSPeriodInfo(date);
  return `Period ${periodNumber} · ${year}`;
};

/**
 * Secondary date-range label shown beneath the main window label.
 * Returns e.g. `"1 November – 28 November"` for same-year periods or
 * `"31 Dec 2027 – 27 Jan 2028"` when the period spans two years.
 * Returns `null` in legacy mode.
 */
export const getCSWindowDateRangeLabel = (date: Date): string | null => {
  if (!isCSPeriodMode(date)) return null;

  const { start, end } = getCSPeriodInfo(date);

  if (getYear(start) !== getYear(end)) {
    return `${formatDate(start, 'd MMM yyyy')} – ${formatDate(end, 'd MMM yyyy')}`;
  }

  return `${formatDate(start, 'd MMMM')} – ${formatDate(end, 'd MMMM')}`;
};

/**
 * The window's heading and its supporting line, already in display order.
 *
 * In period mode the PERIOD NUMBER leads as the heading and the date range supports it:
 * "Period 2 · 2027" is what identifies the window everywhere else in the schedule, and
 * the date range beneath it grounds that number in the calendar.
 * In legacy month mode there is only the heading, exactly as before.
 *
 * Use this rather than pairing {@link getCSWindowLabel} and {@link getCSWindowDateRangeLabel}
 * by hand, so the order stays the same everywhere the pair is rendered.
 */
export const getCSWindowHeading = (date: Date): { heading: string; supporting: string | null } => {
  return { heading: getCSWindowLabel(date), supporting: getCSWindowDateRangeLabel(date) };
};
